/**
 * In-memory routing backend for screens and tests (VITE_USE_MOCK). The team and chat loads come from the mock
 * chat backend (../mockData); shifts are a plausible week built around "today" so the strip always shows people
 * at work, one on a break and one before their shift. The clock for shifts is today 14:07 Bangkok (like the
 * approved mockups), whatever the real time.
 */
import { ApiError } from '../client'
import type { MockDb } from '../mockData'
import { activeShift, addDays, bangkokParts, fromMin, WEEKDAYS_LONG } from '../../lib/routing'
import type {
  LeaveRow,
  MemberState,
  RosterCell,
  RosterLogLine,
  RosterMonth,
  RosterStaff,
  RosterWrite,
  RoutingApi,
  RoutingRole,
  RoutingSettings,
  RoutingValues,
  ShiftRow,
  ShiftWeek,
  ShiftWeekBody,
  StaffLite,
  TeamLoad,
  TeamLoadMember,
} from './types'

const clone = <T>(v: T): T => (v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T))

export const ROUTING_DEFAULTS: RoutingValues = {
  method: 'least_busy',
  sticky: true,
  requeue_off_shift: true,
  sticky_wait_min: 2,
  cap: 10,
  cap_by_staff: {},
  queue_max_wait_min: 3,
  idle_min: 5,
  auto_off_min: 15,
  auto_close_min: 30,
  auto_close_by_channel: { line: 45, comment: 60 },
}

const HEAD_KEYS: (keyof RoutingValues)[] = [
  'auto_off_min',
  'cap',
  'cap_by_staff',
  'idle_min',
  'queue_max_wait_min',
  'sticky_wait_min',
]
const LIMITS: Partial<Record<keyof RoutingValues, [number, number]>> = {
  sticky_wait_min: [1, 10],
  cap: [1, 30],
  queue_max_wait_min: [1, 30],
  idle_min: [2, 30],
  auto_close_min: [5, 1440],
}
const METHODS: RoutingSettings['methods'] = [
  { id: 'least_busy', label: 'ยุ่งน้อยสุด', help: 'คนที่มีแชทรอตอบน้อยสุดได้ก่อน ถ้าเท่ากัน ให้คนที่ว่างนานสุด' },
  { id: 'round_robin', label: 'วนตามลำดับ', help: 'แจกทีละคนวนไปเรื่อยๆ ทุกคนได้จำนวนเท่ากัน' },
  { id: 'manual', label: 'ไม่แจกอัตโนมัติ', help: 'แชทใหม่รอในคิวกลาง ให้ทีมหยิบเอง' },
]
const CHANNELS: RoutingSettings['channels'] = [
  { id: 'fb', label: 'FB' },
  { id: 'ig', label: 'IG' },
  { id: 'line', label: 'LINE' },
  { id: 'comment', label: 'คอมเมนต์' },
  { id: 'web', label: 'เว็บ' },
]
const STATE_LABEL: Record<MemberState, string> = {
  on: 'รับแชท',
  full: 'เต็ม',
  away: 'พัก',
  idle: 'พัก',
  off_shift: 'นอกกะ',
  leave: 'ลา',
}
const MOCK_MINUTE = 14 * 60 + 7
const LABELS: Record<string, string> = {
  method: 'วิธีแจก',
  sticky: 'ลูกค้าเก่าไปคนเดิม',
  requeue_off_shift: 'หมดกะแล้วย้ายแชทเข้าคิวกลาง',
  sticky_wait_min: 'รอคนเดิมไม่เกิน',
  cap: 'แชทสูงสุดต่อคน',
  cap_by_staff: 'แชทสูงสุดรายคน',
  queue_max_wait_min: 'เวลารอในคิวกลางสูงสุด',
  idle_min: 'ไม่ได้เปิดหน้าแชท ถือว่าพัก',
  auto_off_min: 'ปิดพร้อมรับเมื่อไม่มีการใช้งาน',
  auto_close_min: 'ปิดแชทอัตโนมัติ',
  auto_close_by_channel: 'ปิดแชทอัตโนมัติรายช่องทาง',
}

/** Same switch as the mock session (api/session.ts): localStorage `cf-chat.mock-role` = owner | head, else staff. */
export function mockRoutingRole(): RoutingRole {
  try {
    const r = localStorage.getItem('cf-chat.mock-role')
    if (r === 'owner' || r === 'head') return r
  } catch {
    /* no storage (tests) */
  }
  return 'staff'
}

export interface MockRoutingOptions {
  latencyMs?: number
  clock?: () => number
  /** The chat mock's data (team + conversations). */
  db?: () => MockDb | null
  role?: RoutingRole
}

const MONTHS_TH = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน',
  'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม']
const GRID_WD = ['จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส', 'อา']
const READ_ONLY = 'พนักงานดูค่าตั้งได้อย่างเดียว ให้ผู้จัดการหรือเจ้าของแก้'

/** "08:30–16:30" · "หยุด" · "ลา V" (+ " · note") — the change-log text of api/app/routing/shifts.py RosterDay.text */
function cellText(c: RosterCell | undefined): string | null {
  if (!c) return null
  const base = c.kind === 'work' ? `${c.start}–${c.end}` : c.kind === 'leave' ? `ลา ${c.leave}` : 'หยุด'
  return c.note ? `${base} · ${c.note}` : base
}

export class MockRoutingApi implements RoutingApi {
  values: RoutingValues
  shifts: ShiftRow[]
  leave: LeaveRow[]
  role: RoutingRole
  /** ตารางงานรายเดือน (api/app/routing/roster.py) */
  roster: RosterCell[] = []
  rosterLog: (RosterLogLine & { month: string })[] = []
  /** Mock nicknames: the owner's pattern example (บี · หมิว · แป้ง) matches three of the mock team. */
  nicknames: Record<string, string> = { waranya: 'บี', siri: 'หมิว', siriporn: 'แป้ง' }
  updated: RoutingSettings['updated'] = null
  private readonly latency: number
  private readonly now: () => number
  private readonly db: () => MockDb | null

  constructor(opts: MockRoutingOptions = {}) {
    this.latency = opts.latencyMs ?? 120
    this.now = opts.clock ?? (() => Date.now())
    this.db = opts.db ?? (() => null)
    this.role = opts.role ?? 'owner'
    this.values = { ...clone(ROUTING_DEFAULTS), cap_by_staff: { siri: 8, siriporn: 6 } }
    const today = bangkokParts(this.now())
    const staff = this.staff()
    // each person's day off never falls on today; วรัญญา starts later (before-shift at 14:07)
    const plan: [string, string][] = [
      ['09:00', '18:00'],
      ['10:00', '19:00'],
      ['09:00', '18:00'],
      ['16:00', '24:00'],
      ['12:00', '21:00'],
    ]
    this.shifts = []
    staff.forEach((m, i) => {
      const [start, end] = plan[i % plan.length]!
      const off = (today.weekday + i + 1) % 7
      for (let wd = 0; wd < 7; wd++) if (wd !== off) this.shifts.push({ staff_id: m.id, weekday: wd, start, end })
    })
    this.leave = staff[2] ? [{ staff_id: staff[2].id, day: addDays(today.date, 2), note: 'ลากิจ' }] : []
    this.updated = { at: new Date(this.now() - 3 * 3600_000).toISOString(), by: 'จิต' }
  }

  private async delay(): Promise<void> {
    if (this.latency > 0) await new Promise((r) => setTimeout(r, this.latency))
  }

  private staff(): StaffLite[] {
    const team = this.db()?.team ?? [
      { id: 'ploy', name: 'พลอย', initial: 'พ' },
      { id: 'jit', name: 'จิต', initial: 'จ' },
    ]
    return team.map((m) => ({ id: m.id, name: m.name, initial: m.initial }))
  }

  private editable(): (keyof RoutingValues)[] {
    if (this.role === 'owner') return Object.keys(ROUTING_DEFAULTS) as (keyof RoutingValues)[]
    return this.role === 'head' ? [...HEAD_KEYS] : []
  }

  private settings(): RoutingSettings {
    return clone({
      values: this.values,
      defaults: ROUTING_DEFAULTS,
      role: this.role,
      editable: this.editable(),
      head_keys: HEAD_KEYS,
      labels: LABELS,
      methods: METHODS,
      channels: CHANNELS,
      staff: this.staff(),
      shifts_configured: this.shifts.length > 0,
      updated: this.updated,
    })
  }

  async getSettings(): Promise<RoutingSettings> {
    await this.delay()
    return this.settings()
  }

  async saveSettings(changes: Partial<RoutingValues>): Promise<RoutingSettings> {
    await this.delay()
    const keys = Object.keys(changes) as (keyof RoutingValues)[]
    if (!keys.length) throw new ApiError(422, 'ไม่มีค่าที่แก้')
    if (this.role === 'staff') throw new ApiError(403, 'พนักงานดูค่าตั้งได้อย่างเดียว ให้ผู้จัดการหรือเจ้าของแก้')
    if (keys.some((k) => !this.editable().includes(k))) throw new ApiError(403, 'ค่าตั้งนี้เจ้าของเท่านั้นที่แก้ได้')
    for (const k of keys) {
      const lim = LIMITS[k]
      const v = changes[k]
      if (lim && (typeof v !== 'number' || v < lim[0] || v > lim[1])) {
        throw new ApiError(422, `ค่าต้องอยู่ระหว่าง ${lim[0]}–${lim[1]}`)
      }
    }
    const off = changes.auto_off_min
    if (off !== undefined && (typeof off !== 'number' || (off !== 0 && (off < 5 || off > 120)))) {
      throw new ApiError(422, 'ปิดพร้อมรับเมื่อไม่มีการใช้งาน: ต้องเป็น 0 (ไม่ปิดเอง) หรือ 5–120 นาที')
    }
    this.values = { ...this.values, ...clone(changes) }
    // the chat mock's heartbeat uses it (api/mock.ts heartbeat)
    const db = this.db()
    if (off !== undefined && db) db.settings = { ...db.settings, auto_off_min: off }
    this.updated = { at: new Date(this.now()).toISOString(), by: 'คุณ' }
    return this.settings()
  }

  private week(): ShiftWeek {
    const t = bangkokParts(this.now())
    return clone({
      shifts: this.shifts,
      leave: this.leave.filter((l) => l.day >= t.date),
      staff: this.staff(),
      today: t.date,
      weekday: t.weekday,
      now: fromMin(MOCK_MINUTE),
      weekdays: ['จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.', 'อา.'],
      editable: this.role !== 'staff',
    })
  }

  async getShifts(): Promise<ShiftWeek> {
    await this.delay()
    return this.week()
  }

  async saveShifts(body: ShiftWeekBody): Promise<ShiftWeek> {
    await this.delay()
    if (this.role === 'staff') throw new ApiError(403, 'พนักงานดูค่าตั้งได้อย่างเดียว ให้ผู้จัดการหรือเจ้าของแก้')
    for (const s of body.shifts) {
      if (s.start === s.end) throw new ApiError(422, 'เวลาเริ่มกับเวลาเลิกกะต้องไม่ตรงกัน')
    }
    this.shifts = clone(body.shifts)
    this.leave = clone(body.leave)
    this.updated = { at: new Date(this.now()).toISOString(), by: 'คุณ' }
    return this.week()
  }

  // ---------------------------------------------------------------- ตารางงานรายเดือน

  private rosterMonth(month: string): RosterMonth {
    const [y, m] = month.split('-').map(Number) as [number, number]
    const today = bangkokParts(this.now()).date
    const n = new Date(Date.UTC(y, m, 0)).getUTCDate()
    const days = Array.from({ length: n }, (_, i) => {
      const day = `${month}-${String(i + 1).padStart(2, '0')}`
      const weekday = (new Date(Date.UTC(y, m - 1, i + 1)).getUTCDay() + 6) % 7
      return { day, dom: i + 1, weekday, label: GRID_WD[weekday]!, weekend: weekday >= 5, today: day === today }
    })
    const shift = (k: number) => {
      const t = y * 12 + (m - 1) + k
      return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`
    }
    const staff: RosterStaff[] = this.staff().map((p) => ({ ...p, nickname: this.nicknames[p.id] ?? null }))
    return clone({
      month,
      title: `${MONTHS_TH[m - 1]} ${y + 543}`,
      prev: shift(-1),
      next: shift(1),
      this: today.slice(0, 7),
      today,
      days,
      staff,
      cells: this.roster.filter((c) => c.day.startsWith(month)).sort((a, b) => (a.staff_id + a.day).localeCompare(b.staff_id + b.day)),
      weekly: this.shifts,
      leave: this.leave.filter((l) => l.day.startsWith(month)),
      leave_types: [
        { id: 'V', label: 'ลาพักร้อน' },
        { id: 'B', label: 'ลากิจ' },
        { id: 'S', label: 'ลาป่วย' },
      ],
      editable: this.role === 'owner' || (this.role === 'head' && month >= today.slice(0, 7)),
      locked: this.role === 'head' && month < today.slice(0, 7),
      log: this.role === 'staff' ? [] : this.rosterLog.filter((l) => l.month === month).map(({ month: _m, ...l }) => l),
    } satisfies RosterMonth)
  }

  async getRoster(month?: string): Promise<RosterMonth> {
    await this.delay()
    return this.rosterMonth(month ?? bangkokParts(this.now()).date.slice(0, 7))
  }

  async saveRoster(body: RosterWrite): Promise<RosterMonth> {
    await this.delay()
    if (this.role === 'staff') throw new ApiError(403, READ_ONLY)
    if (this.role !== 'owner' && body.month < bangkokParts(this.now()).date.slice(0, 7)) {
      throw new ApiError(403, 'เดือนที่ผ่านไปแล้ว เจ้าของเท่านั้นที่แก้ได้')
    }
    if (!body.cells.length && !body.clear?.length) throw new ApiError(422, 'ไม่มีช่องที่แก้')
    const key = (c: { staff_id: string; day: string }) => `${c.staff_id}|${c.day}`
    const old = new Map(this.roster.filter((c) => c.day.startsWith(body.month)).map((c) => [key(c), c]))
    const next = new Map([...old].filter(([, c]) => !(body.clear ?? []).includes(c.staff_id)))
    let skipped = 0
    for (const e of body.cells) {
      if (!e.day.startsWith(body.month)) throw new ApiError(422, 'วันที่ต้องอยู่ในเดือนที่เลือก')
      if (body.mode === 'fill' && next.has(key(e))) {
        skipped++
        continue
      }
      if (!e.kind) next.delete(key(e))
      else next.set(key(e), { staff_id: e.staff_id, day: e.day, kind: e.kind, start: e.start ?? null, end: e.end ?? null,
        leave: e.leave ?? null, note: e.note ?? '' })
    }
    const changes = [...new Set([...old.keys(), ...next.keys()])]
      .filter((k) => cellText(old.get(k)) !== cellText(next.get(k)))
      .sort()
      .map((k) => ({ staff_id: k.split('|')[0]!, day: k.split('|')[1]!, old: cellText(old.get(k)), new: cellText(next.get(k)) }))
    this.roster = [...this.roster.filter((c) => !c.day.startsWith(body.month)), ...next.values()]
    if (changes.length) {
      this.rosterLog.unshift({ id: String(this.rosterLog.length + 1), month: body.month, at: new Date(this.now()).toISOString(),
        by: 'คุณ', action: body.action ?? 'cells', count: changes.length, changes: changes.slice(0, 8),
        pattern: body.action === 'import' ? (body.pattern ?? null) : null, replaced: !!body.clear?.length })
    }
    return { ...this.rosterMonth(body.month), result: { changed: changes.length, skipped } }
  }

  async setNickname(staffId: string, nickname: string | null): Promise<{ staff_id: string; nickname: string | null; changed: boolean }> {
    await this.delay()
    if (this.role === 'staff') throw new ApiError(403, READ_ONLY)
    const nick = (nickname ?? '').trim().replace(/\s+/g, ' ') || null
    if (nick && nick.length > 30) throw new ApiError(422, 'ชื่อเล่นยาวได้ไม่เกิน 30 ตัวอักษร')
    const changed = (this.nicknames[staffId] ?? null) !== nick
    if (nick) this.nicknames[staffId] = nick
    else delete this.nicknames[staffId]
    return { staff_id: staffId, nickname: nick, changed }
  }

  async teamLoad(): Promise<TeamLoad> {
    await this.delay()
    const db = this.db()
    const nowMs = this.now()
    const today = bangkokParts(nowMs)
    const at = { ...today, minute: MOCK_MINUTE }
    const convs = db ? [...db.conversations.values()] : []
    const waiting = convs.filter((c) => c.status === 'open' && c.needs_reply) // SQL_LOADS (api/app/routing/engine.py)
    const members: TeamLoadMember[] = (db?.team ?? []).map((m) => {
      const load = waiting.filter((c) => c.assigned_staff?.id === m.id).length
      const cap = this.values.cap_by_staff[m.id] ?? this.values.cap
      const mine = this.shifts.filter((s) => s.staff_id === m.id)
      const leaveDays = new Set(this.leave.filter((l) => l.staff_id === m.id).map((l) => l.day))
      const cur = activeShift(mine, leaveDays, at)
      const state: MemberState = !cur
        ? leaveDays.has(today.date)
          ? 'leave'
          : 'off_shift'
        : !m.available
          ? 'away'
          : load >= cap
            ? 'full'
            : 'on'
      const nextStart = mine.filter((s) => s.weekday === today.weekday && s.start > fromMin(MOCK_MINUTE))[0]
      const detail =
        state === 'off_shift'
          ? `${m.name} · นอกกะ${nextStart ? ` · เข้ากะ ${nextStart.start}` : ` · วัน${WEEKDAYS_LONG[today.weekday]}ไม่มีกะ`}`
          : state === 'leave'
            ? `${m.name} · ลาวันนี้ · ไม่ได้รับแชทใหม่`
            : state === 'away'
              ? `${m.name} · กดพักไว้ · ดูแลอยู่ ${load} แชท`
              : state === 'full'
                ? `${m.name} · เต็ม ${load} จาก ${cap} แชท · แชทใหม่เข้าคิวกลาง`
                : `${m.name} · ดูแลอยู่ ${load} จาก ${cap} แชท${cur ? ` · กะ ${cur.start}–${cur.end}` : ''}`
      return {
        id: m.id,
        name: m.name,
        initial: m.initial,
        load,
        cap,
        state,
        state_label: STATE_LABEL[state],
        detail,
        shift_today: cur ? `${cur.start}–${cur.end}` : null,
        last_seen_at: null,
      }
    })
    const queued = waiting.filter((c) => !c.assigned_staff && c.kind === 'dm')
    const oldest = queued.map((c) => c.waiting_since ?? c.last_message_at ?? '').filter(Boolean).sort()[0] ?? null
    const overdue = oldest !== null && nowMs - Date.parse(oldest) >= this.values.queue_max_wait_min * 60000
    const label = METHODS.find((x) => x.id === this.values.method)?.label ?? 'ยุ่งน้อยสุด'
    return clone({
      method: this.values.method,
      method_label: label,
      queue: { waiting: queued.length, oldest_since: oldest, overdue },
      shifts_configured: this.shifts.length > 0,
      members,
      at: new Date(nowMs).toISOString(),
    })
  }
}
